import { Fragment, FunctionComponent, ReactNode, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "primereact/utils";

import { translationKeys } from "../../../../../../models/app-translation-keys";
import { TooltipWrapper } from "../../../../../Shared/Components/TooltipWrapper/TooltipWrapper";

import "./SettingItem.scss";

export interface ISettingItemProps {
  titleKey: string;
  centerVertically?: boolean;
  resetTemplate: ReactNode;
  isDefault: boolean;
  exampleContent?: ReactNode | ((value: any) => ReactNode);
  valueFunction: () => any;
  children: ReactNode;
}

export const SettingItem: FunctionComponent<ISettingItemProps> = (props: ISettingItemProps) => {
  const { t } = useTranslation();

  const exampleTooltipRef = useRef<HTMLDivElement>(null);

  const exampleKeys = translationKeys.adminArea.whiteLabel.example;

  const exampleContentMemo = useMemo(() => {
    if (typeof props.exampleContent === "function") {
      return props.exampleContent(props.valueFunction());
    }

    return props.exampleContent;
  }, [props.exampleContent, props.valueFunction]);

  return (
    <div
      className={classNames("setting-item", {
        "center-vertically": props.centerVertically,
      })}
    >
      <div className="setting-key">{t(props.titleKey)}</div>

      <div
        className={classNames("setting-content", {
          "center-vertically": props.centerVertically,
        })}
      >
        <div className="setting-value">{props.children}</div>

        <div className="setting-reset">{props.resetTemplate}</div>

        {!!props.exampleContent && !props.isDefault && (
          <Fragment>
            <TooltipWrapper targetRef={exampleTooltipRef} content={t(exampleKeys.tooltipInfo)} position="top" />

            <div className="setting-example" ref={exampleTooltipRef}>
              <span>{t(exampleKeys.title)}:</span>

              <div
                role="button"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
              >
                {exampleContentMemo}
              </div>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};
